<template>
    <div class="auth-container">
      <div class="auth-card">
        <div class="auth-header">
          <div class="icon-wrapper">
            <i class="bi bi-shield-lock" v-if="!isProcessing && !isCompleted"></i>
            <div class="spinner" v-if="isProcessing"></div>
            <i class="bi bi-check-circle-fill" v-if="isCompleted && isSuccess"></i>
            <i class="bi bi-exclamation-triangle-fill" v-if="isCompleted && !isSuccess"></i>
          </div>
          <h1 class="auth-title">{{ pageTitle }}</h1>
          <p class="auth-subtitle">{{ pageSubtitle }}</p>
        </div>
  
        <div class="auth-form">
          <!-- Loading State -->
          <div v-if="isProcessing" class="status-section">
            <div class="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p class="status-text">Verifying your reset token...</p>
          </div>
  
          <!-- Success State -->
          <div v-else-if="isCompleted && isSuccess" class="status-section success">
            <div class="success-message">
              <i class="bi bi-check-circle"></i>
              {{ successMessage }}
            </div>
            <div class="next-steps">
              <h4>Next Steps:</h4>
              <ol>
                <li>Check your email for the new password</li>
                <li>Log in with your new password</li>
              </ol>
            </div>
          </div>
  
          <!-- Error State -->
          <div v-else-if="isCompleted && !isSuccess" class="status-section error">
            <div class="error-message">
              <i class="bi bi-exclamation-triangle"></i>
              {{ errorMessage }}
            </div>
            <div class="error-help">
              <h4>What can you do?</h4>
              <ul>
                <li>Check if the link is complete and hasn't been truncated</li>
                <li>Make sure you're using the latest reset link from your email</li>
                <li>Request a new password reset link</li>
              </ul>
            </div>
          </div>
  
          <!-- Initial Invalid State -->
          <div v-else class="status-section error">
            <div class="error-message">
              <i class="bi bi-exclamation-triangle"></i>
              Invalid or missing reset token.
            </div>
            <p class="status-text">Please make sure you're using the correct link from your email.</p>
          </div>
  
          <!-- Action Buttons -->
          <div class="auth-actions">
            <router-link 
              to="/login" 
              class="auth-btn"
              v-if="isCompleted && isSuccess"
            >
              <span>Go to Login</span>
              <i class="bi bi-arrow-right"></i>
            </router-link>
            
            <router-link 
              to="/login" 
              class="auth-btn btn-outline"
              v-if="!isSuccess"
            >
              <i class="bi bi-arrow-left"></i>
              <span>Back to Login</span>
            </router-link>
            
            <button 
              @click="requestNewReset" 
              class="auth-btn btn-outline"
              v-if="isCompleted && !isSuccess"
              style="margin-top: 0.75rem;"
            >
              <i class="bi bi-arrow-clockwise"></i>
              <span>Request New Reset Link</span>
            </button>
          </div>
  
  
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, computed, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import axios from 'axios';
  
  export default {
    name: 'PasswordResetMask',
    props: {
      token: {
        type: String,
        default: null
      }
    },
    setup(props) {
      const router = useRouter();
      
      const isProcessing = ref(false);
      const isCompleted = ref(false);
      const isSuccess = ref(false);
      const errorMessage = ref('');
      const successMessage = ref('');
  
      const pageTitle = computed(() => {
        if (isProcessing.value) return 'Processing Reset Request';
        if (isCompleted.value && isSuccess.value) return 'Password Reset Successful';
        if (isCompleted.value && !isSuccess.value) return 'Password Reset Failed';
        return 'Invalid Reset Link';
      });
  
      const pageSubtitle = computed(() => {
        if (isProcessing.value) return 'Please wait while we verify your request';
        if (isCompleted.value && isSuccess.value) return 'Your password has been successfully reset';
        if (isCompleted.value && !isSuccess.value) return 'We encountered an issue with your request';
        return 'There seems to be a problem with your reset link';
      });
  
      const confirmPasswordReset = async () => {
        if (!props.token) {
          isCompleted.value = true;
          isSuccess.value = false;
          errorMessage.value = 'No reset token provided.';
          return;
        }
  
        isProcessing.value = true;
  
        try {
          // Call the backend endpoint to confirm the password reset
          const response = await axios.get(`/reset-password/confirm/${props.token}`);
          
          isCompleted.value = true;
          isSuccess.value = true;
          
          if (response.data.password) {
            // If password is returned (email sending failed), show it to user
            successMessage.value = `Your new password has been generated. Since email delivery failed, here is your new password: ${response.data.password}`;
          } else {
            // Normal case - password sent via email
            successMessage.value = 'Your password has been reset successfully. A new password has been sent to your email address.';
          }
        } catch (error) {
          console.error('Password reset error:', error);
          isCompleted.value = true;
          isSuccess.value = false;
          
          if (error.response?.status === 400) {
            if (error.response.data.message?.includes('expired')) {
              errorMessage.value = 'This reset link has expired. Please request a new password reset.';
            } else if (error.response.data.message?.includes('Invalid token')) {
              errorMessage.value = 'This reset link is invalid. Please check the link or request a new one.';
            } else {
              errorMessage.value = error.response.data.message || 'Invalid reset request.';
            }
          } else if (error.response?.status === 404) {
            errorMessage.value = 'User not found. Please make sure you\'re using the correct email address.';
          } else {
            errorMessage.value = 'An error occurred while resetting your password. Please try again later.';
          }
        } finally {
          isProcessing.value = false;
        }
      };
  
      const requestNewReset = () => {
        // Redirect to login page where user can request new reset
        router.push('/login');
      };
  
      onMounted(() => {
        confirmPasswordReset();
      });
  
      return {
        isProcessing,
        isCompleted,
        isSuccess,
        errorMessage,
        successMessage,
        pageTitle,
        pageSubtitle,
        requestNewReset
      };
    }
  };
  </script>
  
  <style scoped>
  .auth-container {
    width: 100%;
    max-width: 480px;
    padding: 2rem 1.5rem;
  }
  
  .auth-card {
    background-color: white;
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08), 0 8px 16px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(135, 177, 64, 0.1);
    padding: 2.5rem;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
  }
  
  .auth-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #87B140, #a8d054);
  }
  
  .auth-header {
    text-align: center;
    margin-bottom: 2rem;
  }
  
  .icon-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: #f2f5e7;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    border: 3px solid rgba(135, 177, 64, 0.2);
  }
  
  .icon-wrapper i {
    font-size: 2.5rem;
    color: #87B140;
  }
  
  .icon-wrapper .bi-check-circle-fill {
    color: #28a745;
  }
  
  .icon-wrapper .bi-exclamation-triangle-fill {
    color: #dc3545;
  }
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(135, 177, 64, 0.3);
    border-top-color: #87B140;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  .auth-title {
    font-size: 1.875rem;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 0.5rem;
  }
  
  .auth-subtitle {
    font-size: 1rem;
    color: #718096;
    line-height: 1.5;
    margin: 0;
  }
  
  .auth-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  
  .status-section {
    text-align: center;
  }
  
  .loading-dots {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
  
  .loading-dots span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #87B140;
    animation: loading-dots 1.4s infinite ease-in-out both;
  }
  
  .loading-dots span:nth-child(1) { animation-delay: -0.32s; }
  .loading-dots span:nth-child(2) { animation-delay: -0.16s; }
  
  @keyframes loading-dots {
    0%, 80%, 100% {
      transform: scale(0);
      opacity: 0.5;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
  
  .status-text {
    color: #4a5568;
    font-size: 1rem;
    margin: 0;
  }
  
  .success-message {
    background-color: rgba(40, 167, 69, 0.1);
    color: #28a745;
    padding: 0.75rem 1rem;
    border-radius: 6px;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
  
  .error-message {
    background-color: rgba(220, 53, 69, 0.1);
    color: #dc3545;
    padding: 0.75rem 1rem;
    border-radius: 6px;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
  
  .next-steps, .error-help {
    text-align: left;
    background-color: #f8fafc;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
  }
  
  .next-steps h4, .error-help h4 {
    margin: 0 0 1rem 0;
    font-size: 1rem;
    font-weight: 600;
    color: #2d3748;
  }
  
  .next-steps ol, .error-help ul {
    margin: 0;
    padding-left: 1.5rem;
    color: #4a5568;
  }
  
  .next-steps li, .error-help li {
    margin-bottom: 0.5rem;
    line-height: 1.5;
  }
  
  .auth-actions {
    display: flex;
    flex-direction: column;
    gap: 0;
    align-items: center;
  }
  
  .auth-btn {
    background-color: #87B140;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 12px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 48px;
    text-decoration: none;
    min-width: 200px;
  }
  
  .auth-btn:hover {
    background-color: #75a236;
    transform: translateY(-1px);
    color: white;
    text-decoration: none;
  }
  
  .btn-outline {
    background: transparent !important;
    color: #87B140 !important;
    border: 1px solid #87B140 !important;
  }
  
  .btn-outline:hover {
    background-color: rgba(135, 177, 64, 0.1) !important;
    color: #75a236 !important;
    text-decoration: none;
    transform: translateY(-1px);
  }
  
  .auth-footer {
    text-align: center;
    color: #718096;
    font-size: 0.95rem;
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e2e8f0;
  }
  
  .auth-link {
    color: #87B140;
    font-weight: 600;
    text-decoration: none;
    transition: color 0.3s ease;
  }
  
  .auth-link:hover {
    color: #87B140;
    text-decoration: underline;
  }
  
  @media (max-width: 576px) {
    .auth-container {
      padding: 1rem;
    }
  
    .auth-card {
      padding: 1.5rem;
    }
  
    .auth-title {
      font-size: 1.5rem;
    }
  
    .icon-wrapper {
      width: 70px;
      height: 70px;
    }
    
    .icon-wrapper i {
      font-size: 2rem;
    }
  
    .next-steps, .error-help {
      padding: 1rem;
    }
  
    .auth-btn {
      min-width: 100%;
      padding: 12px;
    }
  }
  </style>